/**
 * Cart Drawer — Purēcha
 * Lightweight AJAX cart using Shopify's /cart.js endpoints.
 *  - Open via [data-cart-open] or any link to /cart
 *  - Close via [data-cart-close], Escape, or backdrop click
 *  - Hydrates from /cart.js on first open
 *  - Re-hydrates after add / change / remove
 *  - Provides global window.PurechaCart.open() and .add()
 */
(function () {
  const drawer = document.getElementById('CartDrawer');
  if (!drawer) return;

  const moneyFormat = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active)
    ? null
    : null;

  function formatMoney(cents) {
    const eur = (cents / 100).toFixed(2).replace('.', ',');
    return eur + ' €';
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  const els = {
    body: drawer.querySelector('[data-cart-body]'),
    loading: drawer.querySelector('[data-cart-loading]'),
    lines: drawer.querySelector('[data-cart-lines]'),
    empty: drawer.querySelector('[data-cart-empty]'),
    foot: drawer.querySelector('[data-cart-foot]'),
    subtotal: drawer.querySelector('[data-cart-subtotal]'),
    countHeadline: drawer.querySelector('[data-cart-count-headline]'),
    checkout: drawer.querySelector('[data-cart-checkout]'),
    ship: drawer.querySelector('[data-cart-ship]'),
    shipLabel: drawer.querySelector('[data-cart-ship-label]'),
    shipValue: drawer.querySelector('[data-cart-ship-value]'),
    shipFill: drawer.querySelector('[data-cart-ship-fill]'),
    upsell: drawer.querySelector('[data-cart-upsell]'),
    upsellAdd: drawer.querySelector('[data-cart-upsell-add]'),
  };

  const FREE_SHIP_THRESHOLD_CENTS = parseInt(drawer.dataset.freeShipThreshold, 10) || 4900;
  const UPSELL_VARIANT_ID = els.upsell ? parseInt(els.upsell.dataset.upsellVariant, 10) : null;

  let productAddPending = false;
  let hydrated = false;
  let cartRequest = null;
  let returnFocus = null;
  let previousOverflow = '';
  drawer.inert = true;

  function setLoading(on) {
    els.loading.hidden = !on;
    if (on) {
      els.lines.hidden = true;
      els.empty.hidden = true;
      els.foot.hidden = true;
    }
  }

  function updateFreeShip(cart) {
    if (!els.ship) return;
    const subtotal = cart.items_subtotal_price != null ? cart.items_subtotal_price : (cart.total_price || 0);
    if (!cart.item_count) {
      els.ship.hidden = true;
      return;
    }
    els.ship.hidden = false;
    const diff = FREE_SHIP_THRESHOLD_CENTS - subtotal;
    if (diff <= 0) {
      els.shipLabel.innerHTML = '<strong>Versand kostenfrei freigeschaltet</strong>';
      els.shipValue.textContent = '✓';
      els.shipFill.style.setProperty('--p', '100%');
      els.ship.dataset.full = 'true';
    } else {
      els.shipLabel.innerHTML = 'Noch <strong>' + formatMoney(diff) + '</strong> bis kostenfreier Versand';
      const pct = Math.max(4, Math.min(100, Math.round(subtotal / FREE_SHIP_THRESHOLD_CENTS * 100)));
      els.shipValue.textContent = formatMoney(subtotal) + ' / ' + formatMoney(FREE_SHIP_THRESHOLD_CENTS);
      els.shipFill.style.setProperty('--p', pct + '%');
      els.ship.dataset.full = 'false';
    }
  }

  function updateUpsell(cart) {
    if (!els.upsell || !UPSELL_VARIANT_ID) return;
    // Hide if cart empty or pump already in cart
    if (!cart.item_count) {
      els.upsell.hidden = true;
      return;
    }
    const alreadyIn = (cart.items || []).some(it => it.variant_id === UPSELL_VARIANT_ID || it.id === UPSELL_VARIANT_ID);
    els.upsell.hidden = alreadyIn;
    // Reset button state on every render
    if (els.upsellAdd) {
      els.upsellAdd.classList.remove('is-added');
      els.upsellAdd.disabled = false;
      const span = els.upsellAdd.querySelector('span');
      if (span) span.textContent = 'Hinzufügen';
    }
  }

  function renderCart(cart) {
    hydrated = true;
    setLoading(false);

    // Update headline count
    const c = cart.item_count || 0;
    els.countHeadline.textContent = c > 0 ? ' · ' + c : '';

    // Update all global cart-count badges
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = c;
      el.dataset.empty = c === 0 ? 'true' : 'false';
    });

    // Free-shipping progress bar
    updateFreeShip(cart);
    // Smart-upsell visibility
    updateUpsell(cart);

    if (c === 0) {
      els.lines.hidden = true;
      els.empty.hidden = false;
      els.foot.hidden = true;
      return;
    }

    // Render line items
    els.lines.innerHTML = cart.items.map(item => {
      const img = item.featured_image && item.featured_image.url
        ? `<img src="${item.featured_image.url}&width=160" alt="${escapeHtml(item.featured_image.alt || item.product_title)}" loading="lazy">`
        : '<div class="cart-line__no-img"></div>';

      const variantTitle = (item.variant_title && item.variant_title !== 'Default Title')
        ? `<div class="cart-line__variant">${escapeHtml(item.variant_title)}</div>` : '';

      const sellingPlan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
        ? `<div class="cart-line__plan">${escapeHtml(item.selling_plan_allocation.selling_plan.name)}</div>` : '';

      const properties = Object.entries(item.properties || {})
        .filter(([key, value]) => !key.startsWith('_') && value !== null && value !== '')
        .map(([key, value]) => `<div class="cart-line__variant"><strong>${escapeHtml(key)}:</strong> ${escapeHtml(String(value))}</div>`).join('');
      const linePrice = formatMoney(item.final_line_price);
      const originalPrice = item.original_line_price > item.final_line_price
        ? `<s class="cart-line__original">${formatMoney(item.original_line_price)}</s>` : '';
      const discounts = (item.line_level_discount_allocations || [])
        .filter(d => d.amount > 0)
        .map(d => `<div class="cart-line__discount">${escapeHtml(d.discount_application.title)} · −${formatMoney(d.amount)}</div>`).join('');
      const unitMeta = item.quantity > 1
        ? `<span class="cart-line__unit">${formatMoney(item.final_price)} / Stk.</span>` : '';

      return `
        <li class="cart-line" data-key="${escapeHtml(item.key)}">
          <a href="${item.url}" class="cart-line__img" data-cart-close>${img}</a>
          <div class="cart-line__main">
            <div class="cart-line__head">
              <a href="${item.url}" class="cart-line__title" data-cart-close>${escapeHtml(item.product_title)}</a>
              <button type="button" class="cart-line__remove" data-cart-remove="${escapeHtml(item.key)}" aria-label="Entfernen">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="6" y1="6" x2="18" y2="18"/><line x1="6" y1="18" x2="18" y2="6"/></svg>
              </button>
            </div>
            ${variantTitle}
            ${sellingPlan}
            ${properties}
            ${discounts}
            <div class="cart-line__foot">
              <div class="cart-line__qty" data-cart-qty>
                <button type="button" data-cart-step="-1" data-cart-key="${escapeHtml(item.key)}" aria-label="Weniger">−</button>
                <span data-cart-qty-value>${item.quantity}</span>
                <button type="button" data-cart-step="1" data-cart-key="${escapeHtml(item.key)}" aria-label="Mehr">+</button>
              </div>
              <div class="cart-line__price">
                ${originalPrice}<strong>${linePrice}</strong>
                ${unitMeta}
              </div>
            </div>
          </div>
        </li>`;
    }).join('');

    els.lines.hidden = false;
    els.empty.hidden = true;
    els.foot.hidden = false;
    els.subtotal.textContent = formatMoney(cart.total_price);
  }

  function fetchCart() {
    if (cartRequest) return cartRequest;
    setLoading(true);
    cartRequest = fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(r => {if(!r.ok)throw new Error('Cart unavailable');return r.json();})
      .then(cart => { renderCart(cart); return cart; })
      .catch(err => { console.error('[Cart] fetch failed', err); setLoading(false); return null; })
      .finally(() => { cartRequest = null; });
    return cartRequest;
  }

  function changeQty(key, qty) {
    setLoading(true);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: qty })
    })
      .then(r => r.json())
      .then(cart => renderCart(cart))
      .catch(err => { console.error('[Cart] change failed', err); setLoading(false); });
  }

  function open(trigger) {
    if(!drawer.classList.contains('is-open')){
      returnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
      previousOverflow = document.documentElement.style.overflow;
    }
    drawer.inert = false;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    if (!hydrated) fetchCart();
    drawer.querySelector('.cart-drawer__close')?.focus({preventScroll:true});
  }

  function close() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = previousOverflow;
    drawer.inert = true;
    if(returnFocus?.isConnected) returnFocus.focus({preventScroll:true});
  }

  // Bind triggers
  document.addEventListener('click', function (ev) {
    // Open triggers
    const openTrigger = ev.target.closest('[data-cart-open], a[href="/cart"], a[href$="/cart"]');
    if (openTrigger && !drawer.contains(openTrigger)) {
      ev.preventDefault();
      open();
      return;
    }
    // Close triggers
    const closeTrigger = ev.target.closest('[data-cart-close]');
    if (closeTrigger && drawer.contains(closeTrigger)) {
      // Links must retain their destination after closing the drawer.
      if (!closeTrigger.matches('a[href]')) ev.preventDefault();
      close();
      return;
    }
    // Remove line
    const removeBtn = ev.target.closest('[data-cart-remove]');
    if (removeBtn) {
      ev.preventDefault();
      changeQty(removeBtn.dataset.cartRemove, 0);
      return;
    }
    // Step qty
    const stepBtn = ev.target.closest('[data-cart-step]');
    if (stepBtn) {
      ev.preventDefault();
      const valueEl = stepBtn.parentElement.querySelector('[data-cart-qty-value]');
      const current = parseInt(valueEl.textContent, 10) || 0;
      const step = parseInt(stepBtn.dataset.cartStep, 10) || 0;
      const next = Math.max(0, current + step);
      changeQty(stepBtn.dataset.cartKey, next);
      return;
    }
    // Checkout
    const checkoutBtn = ev.target.closest('[data-cart-checkout]');
    if (checkoutBtn) {
      ev.preventDefault();
      window.location.href = '/checkout';
      return;
    }
    // Smart-Upsell add
    const upsellBtn = ev.target.closest('[data-cart-upsell-add]');
    if (upsellBtn) {
      ev.preventDefault();
      if (upsellBtn.disabled || !UPSELL_VARIANT_ID) return;
      upsellBtn.disabled = true;
      const span = upsellBtn.querySelector('span');
      const originalLabel = span ? span.textContent : 'Hinzufügen';
      if (span) span.textContent = '...';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: UPSELL_VARIANT_ID, quantity: 1 })
      })
        .then(r => r.json())
        .then(() => {
          if (span) span.textContent = 'Hinzugefügt';
          upsellBtn.classList.add('is-added');
          return fetchCart();
        })
        .catch(err => {
          console.error('[Cart/upsell] failed', err);
          if (span) span.textContent = originalLabel;
          upsellBtn.disabled = false;
        });
      return;
    }
  });

  // Escape to close
  document.addEventListener('keydown', function (ev) {
    if(!drawer.classList.contains('is-open')) return;
    if(ev.key === 'Escape'){ev.preventDefault();close();return;}
    if(ev.key === 'Tab'){
      const focusable=Array.from(drawer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),[tabindex="0"]')).filter(el=>el.getClientRects().length);
      const first=focusable[0],last=focusable[focusable.length-1];
      if(ev.shiftKey && document.activeElement===first){ev.preventDefault();last?.focus();}
      else if(!ev.shiftKey && document.activeElement===last){ev.preventDefault();first?.focus();}
    }
  });

  // ====== Product form AJAX intercept ======
  document.addEventListener('submit', function (ev) {
    const form = ev.target;
    if (ev.defaultPrevented || form.matches('[data-landing-form]')) return;
    if (!form.matches('form[action$="/cart/add"], form.product-form')) return;
    // Skip waitlist / contact forms
    if (form.querySelector('[name="contact[tags]"]')) return;

    ev.preventDefault();
    if (productAddPending) return;
    productAddPending = true;
    const trigger = ev.submitter || document.activeElement;
    const fd = new FormData(form);

    // Sync visible quantity widget into the form before submit (in case the
    // hidden quantity input was not kept in sync by the page JS).
    const qtyDisplay = form.querySelector('[data-qty] span');
    if (qtyDisplay) {
      const visibleQty = parseInt(qtyDisplay.textContent, 10);
      if (visibleQty > 0) fd.set('quantity', visibleQty);
    }

    // Strip empty selling_plan field — Shopify rejects empty string
    if (fd.get('selling_plan') === '' || fd.get('selling_plan') == null) {
      fd.delete('selling_plan');
    }

    const submitButtons = Array.from(document.querySelectorAll('button[type="submit"], input[type="submit"]'))
      .filter(button => button.form && button.form.matches('form[action$="/cart/add"], form.product-form'))
      .map(button => ({ button, disabled: button.disabled }));
    submitButtons.forEach(({ button }) => {
      button.disabled = true;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
    });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: fd
    })
      .then(r => r.json().then(data => ({ ok: r.ok, status: r.status, data })))
      .then(({ ok, status, data }) => {
        if (!ok) {
          // Shopify validation error: data.description + data.message
          const msg = (data && (data.description || data.message)) || 'Konnte nicht in den Warenkorb gelegt werden.';
          showFlash('error', msg);
          console.warn('[Cart/add] ' + status, data);
        } else {
          return fetchCart().then(() => open(trigger));
        }
      })
      .catch(err => {
        console.error('[Cart/add] failed', err);
        showFlash('error', 'Netzwerkfehler. Bitte erneut versuchen.');
      })
      .finally(() => {
        productAddPending = false;
        submitButtons.forEach(({ button, disabled }) => {
          button.disabled = disabled;
          button.classList.remove('is-loading');
          button.removeAttribute('aria-busy');
        });
      });
  }, false);

  function showFlash(kind, message) {
    const existing = document.querySelector('.cart-flash');
    if (existing) existing.remove();
    const el = document.createElement('div');
    el.className = 'cart-flash cart-flash--' + kind;
    el.textContent = message;
    document.body.appendChild(el);
    requestAnimationFrame(() => el.classList.add('is-visible'));
    setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => el.remove(), 320);
    }, 4200);
  }

  // Expose API
  window.PurechaCart = {
    open,
    close,
    refresh: fetchCart,
    add: function (variantId, quantity, sellingPlan) {
      const body = { id: variantId, quantity: quantity || 1 };
      if (sellingPlan) body.selling_plan = sellingPlan;
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      })
        .then(r => r.json())
        .then(() => fetchCart())
        .then(() => open());
    }
  };
})();










